import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
export function useAnchoredPopper(params = {}) {
    const { onRequestClose, closeOnOutsideClick = true, closeOnEscape = true, open } = params;
    const anchorRef = useRef(null);
    const popperRef = useRef(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [placement, setPlacement] = useState('bottom');
    const [popperStyle, setPopperStyle] = useState({
        top: 0,
        left: 0,
    });
    const recomputePlacement = useCallback(() => {
        const pop = popperRef.current;
        const anchor = anchorRef.current ?? pop?.parentElement ?? null;
        if (!pop || !anchor)
            return;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const anchorRect = anchor.getBoundingClientRect();
        const popRect = pop.getBoundingClientRect();
        const measuredHeight = popRect.height;
        const measuredWidth = popRect.width;
        const estimatedHeight = Number.isFinite(measuredHeight) && measuredHeight > 0 ? measuredHeight : 360;
        const estimatedWidth = Number.isFinite(measuredWidth) && measuredWidth > 0 ? measuredWidth : 272;
        const viewportPadding = 8;
        const anchorOffset = 8;
        const spaceBelow = viewportHeight - anchorRect.bottom - anchorOffset;
        const spaceAbove = anchorRect.top - anchorOffset;
        let nextPlacement;
        if (spaceBelow >= estimatedHeight) {
            nextPlacement = 'bottom';
        }
        else if (spaceAbove >= estimatedHeight) {
            nextPlacement = 'top';
        }
        else {
            nextPlacement = spaceAbove >= spaceBelow ? 'top' : 'bottom';
        }
        const centeredLeft = anchorRect.left + anchorRect.width / 2 - estimatedWidth / 2;
        const clampedLeft = Math.min(Math.max(centeredLeft, viewportPadding), Math.max(viewportPadding, viewportWidth - estimatedWidth - viewportPadding));
        const targetTop = nextPlacement === 'bottom'
            ? Math.min(anchorRect.bottom + anchorOffset, Math.max(viewportPadding, viewportHeight - estimatedHeight - viewportPadding))
            : Math.max(anchorRect.top - estimatedHeight - anchorOffset, viewportPadding);
        setPlacement(nextPlacement);
        setPopperStyle({
            top: Math.round(targetTop),
            left: Math.round(clampedLeft),
        });
    }, []);
    const isOpen = useMemo(() => {
        return typeof open === 'boolean' ? open : showCalendar;
    }, [open, showCalendar]);
    useLayoutEffect(() => {
        if (!isOpen)
            return;
        recomputePlacement();
        const raf = window.requestAnimationFrame(recomputePlacement);
        return () => {
            window.cancelAnimationFrame(raf);
        };
    }, [isOpen, recomputePlacement]);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleResize = () => recomputePlacement();
        const handleScroll = () => recomputePlacement();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [recomputePlacement, isOpen]);
    useEffect(() => {
        if (!onRequestClose || !isOpen)
            return;
        const onMouseDown = (e) => {
            if (!closeOnOutsideClick)
                return;
            const target = e.target;
            const pop = popperRef.current;
            if (!pop || !target)
                return;
            const anchor = anchorRef.current ?? pop.parentElement;
            const clickedInsidePopper = pop.contains(target);
            const clickedInsideAnchor = anchor ? anchor.contains(target) : false;
            if (!clickedInsidePopper && !clickedInsideAnchor)
                onRequestClose();
        };
        const onKeyDown = (e) => {
            if (!closeOnEscape)
                return;
            if (e.key === 'Escape')
                onRequestClose();
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onRequestClose, closeOnOutsideClick, closeOnEscape, isOpen]);
    return {
        anchorRef: anchorRef,
        popperRef: popperRef,
        popperStyle,
        showCalendar,
        setShowCalendar,
        placement,
        isOpen,
    };
}
